import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, ArrowLeft, CheckCircle, Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import axios from 'axios';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [code, setCode] = useState(token || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(!!token);
  const [verified, setVerified] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Token varsa doğrula
  useEffect(() => {
    if (token) {
      verifyToken(token);
    }
  }, [token]);

  const verifyToken = async (tokenToVerify) => {
    setVerifying(true);
    setError('');
    
    try {
      const response = await axios.get(`/api/auth/verify-reset-token/${tokenToVerify}`);
      if (response.data.valid) {
        setVerified(true);
        setEmail(response.data.email);
        setCode(tokenToVerify);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Geçersiz veya süresi dolmuş bağlantı');
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!code || code.length < 6) {
      toast.error('Lütfen geçerli bir kod girin');
      return;
    }

    await verifyToken(code);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }

    setLoading(true);

    try {
      await axios.post('/auth/reset-password', {
        token: code,
        new_password: password
      });
      setSuccess(true);
      toast.success('Şifreniz başarıyla güncellendi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Şifre sıfırlama başarısız');
    } finally {
      setLoading(false);
    }
  };

  // Başarılı sıfırlama
  if (success) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card className="border-border/40 shadow-xl">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="mx-auto bg-primary/20 text-primary p-4 rounded-full w-fit mb-6">
                <CheckCircle className="h-8 w-8" />
              </div>
              <h2 className="text-xl font-bold text-white mb-3">Şifre Güncellendi!</h2>
              <p className="text-slate-400 mb-6">
                Şifreniz başarıyla değiştirildi. Artık yeni şifrenizle giriş yapabilirsiniz.
              </p>
              <Link to="/login">
                <Button className="w-full btn-primary">
                  Giriş Yap
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Yükleniyor
  if (verifying) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card className="border-border/40 shadow-xl">
            <CardContent className="pt-8 pb-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-slate-400">Bağlantı doğrulanıyor...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Hata durumu
  if (error && !verified) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card className="border-border/40 shadow-xl">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="mx-auto bg-red-500/20 text-red-400 p-4 rounded-full w-fit mb-6">
                <AlertCircle className="h-8 w-8" />
              </div>
              <h2 className="text-xl font-bold text-white mb-3">Geçersiz Bağlantı</h2>
              <p className="text-slate-400 mb-6">{error}</p>
              <div className="space-y-3">
                <Link to="/sifremi-unuttum">
                  <Button className="w-full btn-primary">
                    Yeni Sıfırlama Bağlantısı Al
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="w-full">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Giriş Sayfasına Dön
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Token doğrulandı - yeni şifre girişi
  if (verified) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card className="border-border/40 shadow-xl">
            <CardHeader className="text-center">
              <div className="mx-auto bg-primary text-primary-foreground p-3 rounded-full w-fit mb-4 glow-green">
                <Lock className="h-6 w-6" />
              </div>
              <CardTitle className="text-2xl font-heading">Yeni Şifre Belirle</CardTitle>
              <CardDescription>
                <span className="text-primary">{email}</span> hesabı için yeni şifre belirleyin.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Yeni Şifre</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="En az 6 karakter"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input-field pr-10"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Şifre Tekrar</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Şifrenizi tekrar girin"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-field"
                    required
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-400">Şifreler eşleşmiyor</p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={loading || password !== confirmPassword}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    'Şifremi Güncelle'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Kod girişi (token yoksa)
  return (
    <div className="min-h-screen bg-brand-dark flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Card className="border-border/40 shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto bg-primary text-primary-foreground p-3 rounded-full w-fit mb-4 glow-green">
              <Lock className="h-6 w-6" />
            </div>
            <CardTitle className="text-2xl font-heading">Şifre Sıfırla</CardTitle>
            <CardDescription>
              E-postanıza gönderilen 6 haneli kodu girin.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Sıfırlama Kodu</Label>
                <Input
                  id="code"
                  type="text"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="input-field text-center text-2xl tracking-widest"
                  maxLength={6}
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={loading || code.length < 6}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Doğrulanıyor...
                  </>
                ) : (
                  'Kodu Doğrula'
                )}
              </Button>
            </form>

            <div className="mt-6 space-y-3 text-center">
              <Link to="/sifremi-unuttum" className="text-sm text-primary hover:underline block">
                Kod almadınız mı? Tekrar gönder
              </Link>
              <Link to="/login" className="text-sm text-slate-400 hover:text-white flex items-center justify-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Giriş sayfasına dön
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
